import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { SDF_ISO_THRESHOLD } from './sdfConstants';
import { trilinearSampleVoxelCenters } from './trilinearSample';

/**
 * Whether the structure surface lies within
 * `searchRadiusMm` of `posVoxel` (fractional voxel
 * coordinates). Volume faces that crop the structure count
 * as surface. Never engages beyond the radius; beyond the
 * SDF band the reach falls short by at most one voxel
 * diagonal.
 */
export function sdfSurfaceWithinRange(
    slices: ReadonlyArray<Uint8Array>,
    sizeVx: ReadonlyVec3,
    resMm: ReadonlyVec3,
    posVoxel: ReadonlyVec3,
    searchRadiusMm: number,
    marginMm: number
): boolean {
    const bytesPerMm = SDF_ISO_THRESHOLD / marginMm;
    // Largest distance a sample can confirm before clamping.
    const bandMm = marginMm - 1 / bytesPerMm;

    if (
        sampleWithinRange(
            slices,
            sizeVx,
            resMm,
            posVoxel,
            Math.min(searchRadiusMm, bandMm),
            bytesPerMm
        )
    ) {
        return true;
    }
    if (searchRadiusMm <= bandMm) {
        return false;
    }
    return probeWithinRange(
        slices,
        sizeVx,
        resMm,
        posVoxel,
        searchRadiusMm,
        bytesPerMm
    );
}

/**
 * Sub-voxel proximity test from a single trilinear sample;
 * valid for radii within the SDF band.
 */
function sampleWithinRange(
    slices: ReadonlyArray<Uint8Array>,
    sizeVx: ReadonlyVec3,
    resMm: ReadonlyVec3,
    posVoxel: ReadonlyVec3,
    radiusMm: number,
    bytesPerMm: number
): boolean {
    // Out-of-box positions sample at the nearest box point;
    // the remaining distance adds to the result.
    const clamped = vec3.fromValues(
        Math.min(Math.max(posVoxel[0], 0.5), sizeVx[0] - 0.5),
        Math.min(Math.max(posVoxel[1], 0.5), sizeVx[1] - 0.5),
        Math.min(Math.max(posVoxel[2], 0.5), sizeVx[2] - 0.5)
    );
    const outsideMm = Math.hypot(
        (posVoxel[0] - clamped[0]) * resMm[0],
        (posVoxel[1] - clamped[1]) * resMm[1],
        (posVoxel[2] - clamped[2]) * resMm[2]
    );

    const value = trilinearSampleVoxelCenters(
        slices,
        sizeVx[0],
        sizeVx[1],
        sizeVx[2],
        clamped[0],
        clamped[1],
        clamped[2]
    );
    const signedMm = (value - SDF_ISO_THRESHOLD) / bytesPerMm;

    const surfaceMm =
        signedMm >= 0
            ? Math.min(signedMm, nearestFaceMm(clamped, sizeVx, resMm))
            : -signedMm;

    return outsideMm + surfaceMm <= radiusMm;
}

/**
 * Proximity test for radii beyond the SDF band: probes
 * voxel centers in the search sphere; `q + |sdf|` at a
 * probe bounds the distance to the surface.
 */
function probeWithinRange(
    slices: ReadonlyArray<Uint8Array>,
    sizeVx: ReadonlyVec3,
    resMm: ReadonlyVec3,
    posVoxel: ReadonlyVec3,
    radiusMm: number,
    bytesPerMm: number
): boolean {
    const nx = sizeVx[0];
    const ny = sizeVx[1];
    const nz = sizeVx[2];
    const rx = resMm[0];
    const ry = resMm[1];
    const rz = resMm[2];
    const px = posVoxel[0];
    const py = posVoxel[1];
    const pz = posVoxel[2];

    const radiusSq = radiusMm * radiusMm;

    const xMin = Math.max(0, Math.floor(px - radiusMm / rx));
    const xMax = Math.min(nx - 1, Math.ceil(px + radiusMm / rx));
    const yMin = Math.max(0, Math.floor(py - radiusMm / ry));
    const yMax = Math.min(ny - 1, Math.ceil(py + radiusMm / ry));
    const zMin = Math.max(0, Math.floor(pz - radiusMm / rz));
    const zMax = Math.min(nz - 1, Math.ceil(pz + radiusMm / rz));

    for (let vz = zMin; vz <= zMax; vz++) {
        const dzMm = (vz + 0.5 - pz) * rz;
        const dzSq = dzMm * dzMm;
        if (dzSq > radiusSq) {
            continue;
        }
        const slice = slices[vz];
        const zFace = vz === 0 || vz === nz - 1;
        for (let vy = yMin; vy <= yMax; vy++) {
            const dyMm = (vy + 0.5 - py) * ry;
            const dyzSq = dyMm * dyMm + dzSq;
            if (dyzSq > radiusSq) {
                continue;
            }
            const rowStart = vy * nx;
            const yzFace = zFace || vy === 0 || vy === ny - 1;
            for (let vx = xMin; vx <= xMax; vx++) {
                const dxMm = (vx + 0.5 - px) * rx;
                const probeSq = dxMm * dxMm + dyzSq;
                if (probeSq > radiusSq) {
                    continue;
                }
                const byte = slice[rowStart + vx];
                const onFace = yzFace || vx === 0 || vx === nx - 1;
                let surfaceMm;
                if (onFace && byte > SDF_ISO_THRESHOLD) {
                    surfaceMm = 0;
                } else if (byte === 0 || byte === 255) {
                    continue;
                } else {
                    surfaceMm = Math.abs(byte - SDF_ISO_THRESHOLD) / bytesPerMm;
                }
                if (Math.sqrt(probeSq) + surfaceMm <= radiusMm) {
                    return true;
                }
            }
        }
    }
    return false;
}

/** Distance to the nearest edge-voxel center plane. */
function nearestFaceMm(
    pos: ReadonlyVec3,
    sizeVx: ReadonlyVec3,
    resMm: ReadonlyVec3
): number {
    let best = Infinity;
    for (let axis = 0; axis < 3; axis++) {
        const toFace =
            Math.min(pos[axis] - 0.5, sizeVx[axis] - 0.5 - pos[axis]) *
            resMm[axis];
        best = Math.min(best, toFace);
    }
    return best;
}
